import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './quiz-prefilled.module.scss';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import { AnswerList, QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import {
    faPencil,
    faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';

const QuizPrefilled = () => {
    const {
        currentQuestion,
        handleSubmitAnswer,
        handleSetQueueQuestions,
        prefillById,
        sectionById,
        state,
    } = useContext(QuizContext);

    const { questionId, questionText, questionExplanation, answerList } =
        currentQuestion;
    const { prefillSectionById, prefillQuestionList } = useMemo(() => {
        if (questionId) {
            const { sectionById, questionList } = prefillById[questionId];

            return {
                prefillSectionById: sectionById,
                prefillQuestionList: questionList,
            };
        } else {
            return { prefillSectionById: {}, prefillQuestionList: [] };
        }
    }, [questionId, prefillById]);

    const [pageTransitioning] = state.pageTransitioning;
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    const [scrolledBottom, setScrolledBottom] = useState(false);

    // Get list of section ids that contain a question without an answer
    const errorSectionIds = useMemo(() => {
        const newErrorSectionIds: (string | number)[] = [];
        Object.values(prefillSectionById).forEach(
            ({
                sectionId: currentSectionId,
                questionList: currentQuestionList,
            }) =>
                currentQuestionList.forEach(
                    ({ prefillAnswers, userAnswers }) => {
                        if (
                            prefillAnswers.length === 0 &&
                            userAnswers.length === 0
                        ) {
                            if (!newErrorSectionIds.includes(currentSectionId))
                                newErrorSectionIds.push(currentSectionId);
                        }
                    },
                ),
        );

        return newErrorSectionIds;
    }, [prefillSectionById]);

    const handleEditAnswers = useCallback(
        (sectionId: string | number) => {
            if (questionId) {
                // Check if section incomplete
                const sectionIncomplete = prefillQuestionList.some(
                    ({ prefillAnswers, userAnswers }) =>
                        prefillAnswers.length === 0 && userAnswers.length === 0,
                );
                handleSetQueueQuestions(
                    questionId,
                    sectionId,
                    sectionIncomplete,
                );
            }
        },
        [questionId, prefillQuestionList, handleSetQueueQuestions],
    );

    // Handle submitting
    const handleSubmit = useCallback(() => {
        if (!continueDisabled && questionId) {
            handleSubmitAnswer({
                questionId: questionId,
                questionText: questionText,
            });
        }
    }, [continueDisabled, questionId, questionText, handleSubmitAnswer]);
    // Continue click listener
    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleSubmit);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleSubmit,
            );
    }, [handleSubmit]);
    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter') handleSubmit();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [answerList, handleSubmit]);
    // Scroll bottom listener
    useEffect(() => {
        const handleSetScrolledBottom = () => {
            setScrolledBottom(true);
        };
        window.addEventListener(
            QUIZ_EVENT_TYPES.scrollBottom,
            handleSetScrolledBottom,
        );

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.scrollBottom,
                handleSetScrolledBottom,
            );
    }, [setScrolledBottom]);
    // Continue state
    useEffect(() => {
        if (!pageTransitioning) {
            let continueDisabled = true;
            if (scrolledBottom) continueDisabled = false;
            if (errorSectionIds.length > 0) continueDisabled = true;
            setContinueDisabled(continueDisabled);
        }
    }, [
        errorSectionIds,
        scrolledBottom,
        pageTransitioning,
        setContinueDisabled,
    ]);

    // Generate comma separated answers
    const getAnswerTexts = ({
        userAnswers,
        prefillAnswers,
    }: {
        userAnswers: AnswerList;
        prefillAnswers: AnswerList;
    }) => {
        let answerTexts = ['-'];
        if (userAnswers.length > 0) {
            answerTexts = userAnswers.map((a) => a.answerText);
        } else if (prefillAnswers.length > 0) {
            answerTexts = prefillAnswers.map((a) => a.answerText);
        }

        return answerTexts;
    };

    return (
        <div className={classNames(styles['quiz-prefilled--wrapper'])}>
            {/* Content header */}
            <div className={classNames(styles['quiz-prefilled-header'])}>
                {/* Title */}
                <h4 className={classNames(styles['title'])}>{questionText}</h4>
                {/* Subtitle */}
                <h5 className={classNames(styles['subtitle'], 'highlight')}>
                    {questionExplanation}
                </h5>
            </div>
            {/* List of sections */}
            <div className={classNames(styles['answer-section-list'])}>
                {/* Section item */}
                {Object.values(prefillSectionById).map(
                    (
                        {
                            sectionId: currentSectionId,
                            questionList: currentQuestionList,
                        },
                        i: number,
                    ) => (
                        <div
                            key={`section-${currentSectionId}-${i}`}
                            className={styles['section-item-wrapper']}
                        >
                            <div
                                className={classNames(
                                    styles['section-item'],
                                    errorSectionIds.includes(currentSectionId)
                                        ? styles['error-field']
                                        : '',
                                )}
                                onClick={() =>
                                    handleEditAnswers(currentSectionId)
                                }
                            >
                                {/* Section header */}
                                <div
                                    className={classNames(
                                        styles['section-header'],
                                    )}
                                >
                                    {/* Section name */}
                                    <h5
                                        className={classNames(
                                            styles['section-name'],
                                        )}
                                    >
                                        {sectionById[currentSectionId]}
                                    </h5>
                                    {/* Edit section */}
                                    <button
                                        className={classNames(
                                            styles['section-edit'],
                                        )}
                                        title="Edit answers"
                                    >
                                        <FontAwesomeIcon icon={faPencil} />
                                    </button>
                                </div>
                                {/* Question list */}
                                <ol
                                    className={classNames(
                                        styles['answer-item-list'],
                                    )}
                                >
                                    {/* Question item */}
                                    {currentQuestionList.map((q) => (
                                        <li
                                            key={q.questionId}
                                            className={classNames(
                                                styles['answer-item'],
                                            )}
                                        >
                                            <span
                                                className={classNames(
                                                    styles['question-name'],
                                                    'p1',
                                                )}
                                            >
                                                {q.questionText}
                                            </span>
                                            {/* Check if answer texts is array or single value */}
                                            {getAnswerTexts(q).length > 1 ? (
                                                <ul>
                                                    {getAnswerTexts(q).map(
                                                        (a, i) => (
                                                            <li
                                                                key={i}
                                                                className={classNames(
                                                                    styles[
                                                                        'question-answer'
                                                                    ],
                                                                    'p1 highlight',
                                                                )}
                                                            >
                                                                {a}
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            ) : (
                                                <span
                                                    className={classNames(
                                                        styles[
                                                            'question-answer'
                                                        ],
                                                        'p1 highlight',
                                                    )}
                                                >
                                                    {getAnswerTexts(q)}
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ol>
                            </div>
                            {errorSectionIds.includes(currentSectionId) && (
                                <p
                                    className={classNames(
                                        styles['error-message'],
                                    )}
                                >
                                    <FontAwesomeIcon
                                        icon={faTriangleExclamation}
                                    />
                                    To proceed, please provide an answer to all
                                    questions in this section
                                </p>
                            )}
                        </div>
                    ),
                )}
            </div>
        </div>
    );
};
export default QuizPrefilled;
